import { inject, ref } from "vue";
import type { AppEmitter } from "../emitter";
import { addCartItem } from "../api/cart";
import { useGoogleDataLayer } from "./useGoogleDataLayer";

/**
 * Put a product's default option in the cart, tracking the one request in flight.
 *
 * Must be called from a component's setup, because it injects the emitter: the nav bar's
 * count only moves on `cartItemUpdated`. `onAdded` runs only when the item actually lands, and
 * is where showing the cart belongs — a button beside a cart already showing it should not.
 */
export function useAddToCart(source: string, onAdded?: () => void) {
    const emitter = inject<AppEmitter>("mitt")!;
    const { pushAddToCartToDataLayer } = useGoogleDataLayer();

    const loading = ref(false);
    const added = ref(false);
    const error = ref("");

    function addToCart(product: any): void {
        if (loading.value) {
            return;
        }

        loading.value = true;

        addCartItem({
            product_option_id: product.default_product_option.id,
            quantity: 1,
        })
            .then((data) => {
                error.value = "";
                added.value = true;

                emitter.emit("cartItemUpdated", data);
                pushAddToCartToDataLayer({
                    product,
                    subscribe: false,
                    quantity: 1,
                    source,
                });

                onAdded?.();
            })
            .catch((failure: any) => {
                if (
                    typeof failure.response !== "undefined" &&
                    Object.prototype.hasOwnProperty.call(
                        failure.response,
                        "data",
                    ) &&
                    Object.prototype.hasOwnProperty.call(
                        failure.response.data,
                        "errors",
                    )
                ) {
                    const field = Object.keys(failure.response.data.errors)[0];
                    error.value = failure.response.data.errors[field][0];
                }
            })
            .finally(() => (loading.value = false));
    }

    return {
        loading,
        added,
        error,
        addToCart,
    };
}
